<script setup lang="ts">
  import { computed } from "vue";
  import { storeToRefs } from "pinia";
  import { Check, Copy, Maximize2, Pencil, TriangleAlert, Undo2 } from "lucide-vue-next";
  import { Button } from "@/components/ui/button";
  import { Badge } from "@/components/ui/badge";
  import { Tooltip } from "@/components/ui/tooltip";
  import { useMAssetLibraryStore } from "@stores/mAssetLibrary";
  import { placementWarning } from "@utils/media";
  import ImageLibraryAssetThumbnail from "@/components/MCampaign/ImageLibrary/ImageLibraryAssetThumbnail.vue";
  import type { MMediumUpload } from "@/types";

  const props = defineProps<{ image: MMediumUpload }>();

  const assetLibraryStore = useMAssetLibraryStore();
  const { mode, membership, selectedIds, inUseIds, targetPlacements } = storeToRefs(assetLibraryStore);
  const { toggleSelection, setEditedAsset, restoreToLibrary, openLightbox, recordDimensions, dimensionsOf } =
    assetLibraryStore;

  const isSelected = computed(() => selectedIds.value.includes(props.image.id));

  const usageCount = computed(() => props.image.used_in_creatives_count ?? 0);
  const usageLabel = computed(() => {
    if (mode.value === "picker") return inUseIds.value.includes(props.image.id) ? "In use" : null;
    return usageCount.value > 0 ? `In ${usageCount.value} creative${usageCount.value > 1 ? "s" : ""}` : null;
  });

  const copiesCount = computed(() => props.image.same_url_count ?? 1);

  const ratioWarning = computed(() =>
    mode.value === "picker" ? placementWarning(dimensionsOf(props.image), targetPlacements.value) : null,
  );

  const visibleTags = computed(() => (props.image.asset_tags || []).slice(0, 2));

  const openPreview = () => openLightbox(props.image.id);
  const openEdit = () => setEditedAsset(props.image);
  const putBack = () => restoreToLibrary([props.image.id]);
</script>

<template>
  <div
    class="group relative cursor-pointer overflow-hidden rounded-md border-2 bg-card transition-colors"
    :class="isSelected ? 'border-primary ring-2 ring-primary/40' : 'border-border hover:border-muted-foreground/40'"
    @click="toggleSelection(image.id)">
    <div class="relative aspect-square w-full">
      <ImageLibraryAssetThumbnail
        :asset="image"
        show-play-badge
        @measured="(dimensions) => recordDimensions(image.id, dimensions)" />

      <div
        v-if="isSelected"
        class="absolute left-2 top-2 flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground">
        <Check class="h-4 w-4" />
      </div>

      <Tooltip
        v-if="ratioWarning"
        :content="ratioWarning">
        <span
          class="absolute bottom-2 left-2 flex h-6 w-6 items-center justify-center rounded-full bg-status-warning-soft text-status-warning-strong"
          @click.stop>
          <TriangleAlert class="h-3.5 w-3.5" />
        </span>
      </Tooltip>

      <div class="absolute right-2 top-2 flex gap-1 group-hover:hidden">
        <Badge
          v-if="copiesCount > 1"
          variant="secondary"
          class="gap-1 text-xs">
          <Copy class="h-3 w-3" />
          ×{{ copiesCount }}
        </Badge>
        <Badge
          v-if="usageLabel"
          variant="default"
          class="text-xs">
          {{ usageLabel }}
        </Badge>
      </div>

      <div class="absolute right-2 top-2 hidden gap-1 group-hover:flex">
        <Button
          v-if="membership === 'out'"
          variant="secondary"
          size="icon-sm"
          title="Put back in library"
          @click.stop="putBack">
          <Undo2 class="h-3.5 w-3.5" />
        </Button>
        <Button
          variant="secondary"
          size="icon-sm"
          title="Preview"
          @click.stop="openPreview">
          <Maximize2 class="h-3.5 w-3.5" />
        </Button>
        <Button
          variant="secondary"
          size="icon-sm"
          title="Edit"
          @click.stop="openEdit">
          <Pencil class="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>

    <div class="space-y-1 p-2">
      <p class="truncate text-2xs font-medium leading-tight">{{ image.name }}</p>
      <div
        v-if="visibleTags.length"
        class="flex flex-wrap gap-1">
        <Badge
          v-for="tag in visibleTags"
          :key="tag"
          variant="outline"
          class="text-3xs font-normal">
          {{ tag }}
        </Badge>
      </div>
    </div>
  </div>
</template>
